import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Formik,
  Field,
  FormikProps,
  FieldArray,
  FieldArrayRenderProps,
} from "formik";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { Loading } from "../../../components/Loading";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { vehicleExpenseApi } from "../../vehicleExpense/apiService";
import ExpenseForm from "../../vehicleExpense/components/ExpenseForm";
import {
  ExpenseEntry,
  ExpenseFormData,
  getEmptyExpenseEntry,
  mapExpenseEntryToApiRequest,
} from "../../vehicleExpense/components/types";
import Grid from "@mui/material/Grid";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useToast } from "../../../components/Toast";
import Divider from "@mui/material/Divider";
import { handleFixedExpenseSubmit } from "../utils/handleFixedExpenseSubmit";
import { fixedIncomeApi } from "../apiService";
import DeletedExpenseDetails from "../../rentalBooking/components/DeletedExpenseDetails";

const EXPENSE_TYPES = [
  "Fuel",
  "Maintenance / Repair",
  "Insurance",
  "Driver/Cleaner",
  "Parking",
  "Challan",
  "Other",
] as const;

interface FixedExpenseProps {
  selectedVehicle: any;
  fixedJourneyId?: string;
  setExpenseData: (data: any) => void;
}

// Add transformation function for backend expense data
const transformBackendExpenseToFormData = (
  backendExpense: any
): ExpenseEntry => {
  // Map backend expense types to frontend types
  const expenseTypeMap: Record<string, string> = {
    FUEL: "Fuel",
    REPAIR: "Maintenance / Repair",
    DRIVER_CLEANER: "Driver/Cleaner",
    PARKING: "Parking",
    OTHER: "Other",
    INSURANCE: "Insurance",
    CHALLAN: "Challan",
  };

  const mappedType = expenseTypeMap[backendExpense.expenseType] || "Other";

  const transformedExpense: ExpenseEntry = {
    id: backendExpense.id,
    type: mappedType,
    expenseDate: backendExpense.expenseDate,
    amount: backendExpense.amount,
    paidTo: backendExpense.paidTo || "",
    location: backendExpense.location || "",
    contactNumber: backendExpense.contactNumber || "",
    notes: backendExpense.notes || "",
    isEdited: false,
    isDeleted: false,

    // Initialize with empty defaults
    payments: backendExpense.payments || [],
    deletedPayments: backendExpense.deletedPayments || [],
    remainingAmount: 0,
    fuelType: "",
    liters: "",
    pricePerLitre: "",
    bulkDiscountApplied: "",
    petrolPumpId: "",
    odometerReading: "",
    serviceType: "",
    serviceProvider: "",
    serviceProviderId: "",
    maintenanceItem: "",
    customMaintenanceItem: "",
    warrantyPeriod: "",
    warrantyStartDate: "",
    warrantyEndDate: "",
    customWarrantyPeriod: "",
    nextServiceDue: "",
    invoiceNumber: "",
    workOrderNumber: "",
    category: undefined,
    referenceId: "",
    customCategory: "",
    challanAction: "",
    updatedAt: backendExpense.updatedAt || "",
    deletedDetails: backendExpense.deletedDetails || [],
  };

  // Fill in specific details based on expense type and details
  if (backendExpense.details) {
    const details = backendExpense.details;

    if (backendExpense.expenseType === "FUEL" && details) {
      transformedExpense.fuelType = details.fuelType || "";
      transformedExpense.liters = details.liters || "";
      transformedExpense.pricePerLitre = details.pricePerLitre || "";
      transformedExpense.bulkDiscountApplied =
        details.bulkDiscountApplied || "";
      transformedExpense.petrolPumpId = details.petrolPumpId || "";
      transformedExpense.odometerReading =
        details.odometerReading?.toString() || "";
    }

    if (
      (backendExpense.expenseType === "DRIVER_CLEANER" ||
        backendExpense.expenseType === "PARKING" ||
        backendExpense.expenseType === "OTHER") &&
      details
    ) {
      transformedExpense.category = details.category;
      transformedExpense.referenceId = details.referenceId || "";
      transformedExpense.customCategory = details.customCategory || "";
    }

    if (backendExpense.expenseType === "CHALLAN" && details) {
      transformedExpense.challanAction = details.challanAction || "";
      transformedExpense.responsibleParty = details.responsibleParty || "";
      transformedExpense.responsiblePartyContact =
        details.responsiblePartyContact || "";
      transformedExpense.responsiblePartyName =
        details.responsiblePartyName || "";
      transformedExpense.challanNumber = details.challanNumber || "";
      transformedExpense.challanType = details.challanType || "";
    }

    if (
      ["REPAIR", "MAINTENANCE"].includes(backendExpense.expenseType) &&
      details
    ) {
      transformedExpense.maintenanceItem = details.maintenanceItem || "";
      transformedExpense.customMaintenanceItem =
        details.customMaintenanceItem || "";
      transformedExpense.warrantyPeriod = details.warrantyPeriod || "";
      transformedExpense.warrantyStartDate = details.warrantyStartDate || "";
      transformedExpense.warrantyEndDate = details.warrantyEndDate || "";
      transformedExpense.customWarrantyPeriod =
        details.customWarrantyPeriod || "";
      transformedExpense.nextServiceDue = details.nextServiceDue || "";
      transformedExpense.invoiceNumber = details.invoiceNumber || "";
      transformedExpense.workOrderNumber = details.workOrderNumber || "";
      transformedExpense.odometerReading = details.odometerReading || "";
      transformedExpense.actionType = details.actionType || "";
      transformedExpense.serviceProviderId = details.serviceProviderId || "";
      transformedExpense.serviceType = details.serviceType || "";
    }
  }

  return transformedExpense;
};

const FixedExpense: React.FC<FixedExpenseProps> = ({
  selectedVehicle,
  fixedJourneyId,
  setExpenseData,
}) => {
  const defaultInitialValues: ExpenseFormData = {
    vehicleNumber: selectedVehicle?.vehicleNumber || "",
    vehicleId: selectedVehicle?.vehicleId || "",
    expenses: [],
  };

  const navigate = useNavigate();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] =
    useState<ExpenseFormData>(defaultInitialValues);
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [changedExpenses, setChangedExpenses] = useState<ExpenseEntry[]>([]);
  const [showChangeLogs, setShowChangeLogs] = useState(false);
  const { showToast } = useToast();

  // Update initial values when selectedVehicle changes
  useEffect(() => {
    if (selectedVehicle) {
      setInitialValues((prev) => ({
        ...prev,
        vehicleNumber: selectedVehicle.vehicleNumber || "",
        vehicleId: selectedVehicle.vehicleId || "",
      }));
    }
  }, [selectedVehicle]);

  const fetchExpenseDetails = async () => {
    try {
      setIsLoading(true);
      // For fixed income, we might need different API endpoints
      // This is a placeholder for now - adjust based on your API structure
      let response = await Promise.all([
        fixedIncomeApi.getExpenseDetails(id! || fixedJourneyId!),
        fixedIncomeApi.getDeletedExpenseDetails(id! || fixedJourneyId!),
      ]);
      const [expenseDetails, deletedExpenseDetails] = response;

      if (expenseDetails && Array.isArray(expenseDetails)) {
        const transformedExpenses = expenseDetails.map(
          transformBackendExpenseToFormData
        );

        setInitialValues({
          vehicleNumber: selectedVehicle?.vehicleNumber || "",
          vehicleId: selectedVehicle?.vehicleId || "",
          expenses: transformedExpenses,
        });
        setExpenseData(transformedExpenses);
        setHasLoadedData(true);
      }
      if (deletedExpenseDetails && Array.isArray(deletedExpenseDetails)) {
        setChangedExpenses(deletedExpenseDetails);
      }
    } catch (error) {
      console.error("Error fetching expense details:", error);
      // Keep default values on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchExpenseDetails();
    }
  }, [id]);

  const renderExpenseTypeSelector = (
    arrayHelpers: FieldArrayRenderProps,
    expenses: ExpenseEntry[]
  ) => {
    const existingTypes = new Set(expenses.map((expense) => expense.type));

    // Filter out expense types that are already added
    const availableTypes = EXPENSE_TYPES.filter(
      (type) => !existingTypes.has(type)
    );

    if (availableTypes.length === 0) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            position: "sticky",
            top: 24,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            All expense types added
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the "Add Another" button in each section to add more expenses of
            the same type.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          position: "sticky",
          top: 24,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Select expense type to add:
        </Typography>
        <Stack direction="column" spacing={1}>
          {availableTypes.map((type) => (
            <Chip
              key={type}
              label={
                <span style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                  {type}
                </span>
              }
              onClick={() => arrayHelpers.push(getEmptyExpenseEntry(type))}
              color="primary"
              variant="outlined"
              icon={<AddIcon />}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "auto",
                py: 1,
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  textAlign: "left",
                  pl: 1,
                },
                fontSize: "0.75rem",
              }}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  const renderExpensesByType = (
    type: string,
    expenses: ExpenseEntry[],
    arrayHelpers: FieldArrayRenderProps,
    formikProps: FormikProps<ExpenseFormData>
  ) => {
    const typeExpenses = expenses.filter((expense) => expense.type === type);
    const startIndex = expenses.findIndex((expense) => expense.type === type);

    if (typeExpenses.length === 0) return null;

    return (
      <Box key={type} sx={{ mb: 4 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          {type} Expenses
        </Typography>

        {typeExpenses.map((_, typeIndex) => {
          const index = startIndex + typeIndex;
          return (
            <ExpenseForm
              key={index}
              type={type}
              index={index}
              typeIndex={typeIndex}
              onDelete={() => {
                formikProps.setFieldValue(`expenses.${index}.isDeleted`, true);
              }}
              formikProps={formikProps}
            />
          );
        })}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => arrayHelpers.push(getEmptyExpenseEntry(type))}
            variant="outlined"
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: "30px",
            }}
          >
            Add Another {type} Expense
          </Button>
        </Box>
      </Box>
    );
  };

  const validateStep = (values: ExpenseFormData) => {
    return values.expenses.length > 0;
  };

  if (isSubmitting) {
    return <Loading message="Submitting..." fullScreen={true} />;
  }

  if (isLoading) {
    return <Loading message="Loading expenses..." fullScreen={true} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={(values) =>
          handleFixedExpenseSubmit(
            values,
            initialValues,
            id!,
            fixedJourneyId!,
            setIsSubmitting,
            showToast,
            () => {
              navigate("/fixed-income");
            },
            selectedVehicle
          )
        }
      >
        {(formikProps: FormikProps<ExpenseFormData>) => {
          return (
            <Box
              component="form"
              noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                formikProps.handleSubmit(e);
              }}
            >
              {authLoading ? (
                <Loading message="Loading..." fullScreen={true} />
              ) : (
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        p: 2,
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle2">Total Amount</Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontStyle: "italic" }}
                      >
                        ₹
                        {formikProps.values.expenses
                          .reduce(
                            (acc, expense) => acc + Number(expense.amount),
                            0
                          )
                          .toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
              <FieldArray name="expenses">
                {(arrayHelpers: FieldArrayRenderProps) => (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                      {EXPENSE_TYPES.map((type) =>
                        renderExpensesByType(
                          type,
                          formikProps.values.expenses,
                          arrayHelpers,
                          formikProps
                        )
                      )}
                      {changedExpenses?.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />

                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "gray",
                                fontSize: "0.75rem",
                              }}
                              onClick={() => {
                                setShowChangeLogs(!showChangeLogs);
                              }}
                            >
                              Expense Change Logs
                              {showChangeLogs ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </Typography>
                            {showChangeLogs && (
                              <>
                                <DeletedExpenseDetails
                                  deletedExpenseDetails={changedExpenses}
                                />
                              </>
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {renderExpenseTypeSelector(
                        arrayHelpers,
                        formikProps.values.expenses
                      )}
                    </Grid>
                  </Grid>
                )}
              </FieldArray>
              {/* Navigation Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 2,
                  // mr: 2,
                  pt: 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  size="small"
                  sx={{ fontSize: "0.75rem", px: 3 }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

export default FixedExpense;
